/**
 * A table of records.
 *
 * A real `<table>`, not a grid of divs. The element is what tells assistive
 * technology that a cell belongs to a column and a row, which is the entire
 * value of a table; a div grid reads as an undifferentiated wall of text and
 * has to reconstruct all of it with ARIA that is easy to get subtly wrong.
 *
 * The `caption` is required rather than optional. It is what a screen-reader
 * user hears when they land on the table, and "table with 8 columns" tells
 * them nothing about which table they are in.
 *
 * Loading, error and empty are handled here rather than by each caller, for the
 * reason given in `states.tsx`: a state each screen must remember to render is
 * a state some screen will forget.
 */

import type { ReactNode } from "react";

import { EmptyState, ErrorState, LoadingState } from "../states/states";
import "./data.css";

export interface Column<Row> {
  /** Stable key, used for React and for the column's identity. */
  key: string;
  /** Translated by the caller. */
  header: string;
  render: (row: Row) => ReactNode;
  /** Right-align numeric columns; anything else reads badly. */
  align?: "start" | "end";
  /** Hidden below the narrow breakpoint. Use for secondary detail only. */
  secondary?: boolean;
  /** Shown beside the header: the column's menu (`ColumnMenu`). */
  menu?: ReactNode;
}

/**
 * How tall a row is.
 *
 * Three steps rather than a free number, because §4 of the UX specification
 * names three and because a table whose density is a pixel value is a table
 * every screen sets differently. `standard` is the default everywhere; a
 * screen chooses `compact` when the reader's job is to scan a long list, and
 * `comfortable` when a row carries two lines.
 */
export type DataTableDensity = "standard" | "compact" | "comfortable";

export interface DataTableProps<Row> {
  caption: string;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  loading?: boolean;
  /**
   * The last read failed.
   *
   * `retryLabel` travels with `onRetry` because this component cannot
   * translate — see `ErrorState`, where an English default reached a French
   * customer's screen.
   */
  error?:
    | ({ title: string; description?: string } & (
        | { onRetry: () => void; retryLabel: string }
        | { onRetry?: undefined; retryLabel?: undefined }
      ))
    | undefined;
  empty: { title: string; description?: string };
  loadingLabel: string;
  density?: DataTableDensity;
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  empty,
  loadingLabel,
  density = "standard",
}: DataTableProps<Row>) {
  // Order matters: an error takes precedence over a stale list, and a first
  // load shows the spinner rather than an empty state that would read as
  // "there is nothing" when the truth is "we do not know yet".
  if (error) {
    return (
      <ErrorState
        title={error.title}
        {...(error.description !== undefined ? { description: error.description } : {})}
        {...(error.onRetry !== undefined
          ? { onRetry: error.onRetry, retryLabel: error.retryLabel }
          : {})}
      />
    );
  }
  if (loading && rows.length === 0) return <LoadingState label={loadingLabel} />;
  // A column filter lives in its header. Replacing the whole table by the empty state would
  // take away the very filter that emptied it, with nothing left to undo it but a reload — so
  // when a column has a menu, the headers stay and the empty state goes inside the table.
  const keepHeaders = columns.some((column) => column.menu);
  if (rows.length === 0 && !keepHeaders) {
    return (
      <EmptyState
        title={empty.title}
        {...(empty.description !== undefined ? { description: empty.description } : {})}
      />
    );
  }

  return (
    <div className="hardauto-table__scroll">
      {/* `aria-busy` marks a refresh in progress without replacing the rows
          the user is already reading. */}
      <table
        className="hardauto-table"
        // Absent at the default, so the attribute in the DOM always means
        // somebody chose something.
        data-density={density === "standard" ? undefined : density}
        aria-busy={loading || undefined}
      >
        <caption className="hardauto-table__caption">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                data-align={column.align ?? "start"}
                data-secondary={column.secondary ? "true" : undefined}
              >
                {column.header}
                {column.menu}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState
                  title={empty.title}
                  {...(empty.description !== undefined ? { description: empty.description } : {})}
                />
              </td>
            </tr>
          ) : null}
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  data-align={column.align ?? "start"}
                  data-secondary={column.secondary ? "true" : undefined}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
